<script setup lang="ts">
  import { computed, inject, Ref } from 'vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { IClubProgramDraft } from './club-program-draft';

  const draft = inject<Ref<IClubProgramDraft>>('clubProgramDraft')!;
  const restaurantStore = useRestaurantStore();

  const branches = computed(() => restaurantStore.branches ?? []);
  const showBranches = computed(() => branches.value.length > 1);

  const allBranches = computed({
    get: () => draft.value.branchIds === null,
    set: (value: boolean) => {
      draft.value.branchIds = value ? null : branches.value.map((b) => b.id);
    },
  });

  function isBranchSelected(branchId: number): boolean {
    return (
      draft.value.branchIds === null ||
      draft.value.branchIds.includes(branchId)
    );
  }

  function toggleBranch(branchId: number) {
    if (draft.value.branchIds === null) {
      // Leaving "all": start from every branch minus the clicked one.
      draft.value.branchIds = branches.value
        .map((b) => b.id)
        .filter((id) => id !== branchId);
      return;
    }

    const idx = draft.value.branchIds.indexOf(branchId);
    if (idx > -1) draft.value.branchIds.splice(idx, 1);
    else draft.value.branchIds.push(branchId);
  }
</script>

<template>
  <div class="club-step-activate">
    <p class="step-intro">{{ $t('club.wizard.activateIntro') }}</p>

    <div class="step-field">
      <label class="step-label">{{ $t('club.wizard.nameLabel') }}</label>
      <PosUiInput
        :value="draft.name"
        :maxlength="64"
        :placeholder="$t('club.wizard.namePlaceholder')"
        @input="draft.name = $event"
      />
      <span class="step-hint">{{ $t('club.wizard.nameHint') }}</span>
    </div>

    <div v-if="showBranches" class="step-field">
      <label class="step-label">{{ $t('club.wizard.branchesLabel') }}</label>

      <label class="branch-row">
        <input
          v-model="allBranches"
          class="branch-checkbox"
          type="checkbox"
        />
        <span class="branch-name">{{ $t('club.wizard.allBranches') }}</span>
      </label>

      <template v-if="!allBranches">
        <label
          v-for="branch in branches"
          :key="branch.id"
          class="branch-row indented"
        >
          <input
            class="branch-checkbox"
            type="checkbox"
            :checked="isBranchSelected(branch.id)"
            @change="toggleBranch(branch.id)"
          />
          <span class="branch-name">{{ branch.name }}</span>
        </label>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .club-step-activate {
    @apply tw-flex tw-flex-col tw-gap-4;

    .step-intro {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }

    .step-field {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .step-label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-700;
      }

      .step-hint {
        @apply tw-text-xs tw-text-neutral-400;
      }

      .branch-row {
        @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-lg tw-px-2 tw-py-1.5 tw-transition-colors hover:tw-bg-neutral-50;

        &.indented {
          @apply tw-ml-6;
        }

        .branch-checkbox {
          @apply tw-h-5 tw-w-5 tw-accent-orange-600;
          width: 18px;
          height: 18px;
        }

        .branch-name {
          @apply tw-text-sm tw-text-neutral-800;
        }
      }
    }
  }
</style>
